/**
 * KudosButton — compact heart/trophy button that lets any signed-in user
 * congratulate a graduate on a cert or an Ambassador milestone.
 *
 * Props:
 *   - targetType: "graduate_cert" | "ambassador"
 *   - targetId:   cert_number or user_id (respectively)
 *   - initialCount, initialGiven: optional preload from batch summary
 *   - size: "sm" | "md" (default md)
 *   - onCountChange: optional callback (nextCount, nextGiven)
 *
 * Non-authed users see a disabled state that prompts login on click.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart } from 'lucide-react';
import { AuthContext } from '../App';
import { toast } from 'sonner';

const ANON_KEY = (t, id) => `fda_kudos_${t}_${id}`;

const KudosButton = ({
  targetType,
  targetId,
  initialCount = 0,
  initialGiven = false,
  size = 'md',
  onCountChange,
  className = '',
  stopPropagation = true,
}) => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [count, setCount] = useState(initialCount);
  const [given, setGiven] = useState(initialGiven);
  const [pending, setPending] = useState(false);
  const [pop, setPop] = useState(false);

  // Anonymous visitors: remember "already cheered" client-side so the button
  // reflects state on repeat visits (server also dedupes by IP).
  useEffect(() => {
    if (user || initialGiven) return;
    try {
      if (window.localStorage.getItem(ANON_KEY(targetType, targetId)) === '1') {
        setGiven(true);
      }
    } catch (_) { /* ignore */ }
  }, [user, targetType, targetId, initialGiven]);

  // Keep local state in sync when parent supplies fresh initialCount/initialGiven.
  useEffect(() => { setCount(initialCount); }, [initialCount]);
  useEffect(() => { setGiven(initialGiven); }, [initialGiven]);

  const handleClick = async (e) => {
    if (stopPropagation) { e.preventDefault(); e.stopPropagation(); }
    if (pending) return;
    // Anonymous users who have already cheered can't un-cheer — friendly hint.
    if (!user && given) {
      toast.info('You already cheered this one — thanks!');
      return;
    }
    setPending(true);
    const nextGiven = user ? !given : true;
    const nextCount = Math.max(0, count + (nextGiven && !given ? 1 : (user && given && !nextGiven ? -1 : 0)));
    setGiven(nextGiven);
    setCount(nextCount);
    if (nextGiven && !given) { setPop(true); setTimeout(() => setPop(false), 400); }
    try {
      const { data } = await axios.post(`${API}/kudos/toggle`, {
        target_type: targetType,
        target_id: targetId,
      });
      setGiven(!!data?.given);
      setCount(typeof data?.count === 'number' ? data.count : nextCount);
      onCountChange && onCountChange(data?.count ?? nextCount, !!data?.given);
      if (!user && data?.given) {
        try { window.localStorage.setItem(ANON_KEY(targetType, targetId), '1'); } catch (_) { /* ignore */ }
      }
    } catch (_) {
      setGiven(given);
      setCount(count);
      toast.error('Could not send kudos');
    } finally {
      setPending(false);
    }
    // Nudge non-authed users to sign up on their first cheer — subtle, not intrusive.
    if (!user && !given) {
      toast.success('Cheer sent 🎉  Sign up to earn your own badges', {
        action: { label: 'Join', onClick: () => navigate('/login') },
        duration: 6000,
      });
    }
  };

  const isSm = size === 'sm';
  const base = isSm ? 'h-7 px-2 text-[11px]' : 'h-8 px-2.5 text-xs';
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={given}
      aria-label={given ? 'Remove kudos' : 'Send kudos'}
      data-testid={`kudos-btn-${targetType}-${targetId}`}
      className={`inline-flex items-center gap-1.5 rounded-full font-bold transition-all ${base}
        ${given
          ? 'bg-rose-500/15 text-rose-600 border border-rose-500/40 hover:bg-rose-500/25'
          : 'bg-card border border-border text-foreground/75 hover:text-rose-600 hover:border-rose-500/40'
        }
        ${pending ? 'opacity-60 cursor-wait' : ''}
        ${pop ? 'scale-110' : ''}
        ${className}`}
    >
      <Heart className={`${isSm ? 'w-3 h-3' : 'w-3.5 h-3.5'} ${given ? 'fill-current' : ''}`} />
      <span className="tabular-nums">{count}</span>
    </button>
  );
};

export default KudosButton;
